/**
 * The gym's working state, for the people who run it.
 *
 * 🔴 WHY THIS EXISTS AT ALL. The public page withholds which feed contributed what, on purpose: a
 * finding is a claim about a population, and naming contributors publishes an audience's behaviour
 * to anyone who asks. That is right, and it leaves the one question the gym's operators actually
 * have — IS THIS ONE FEED OR THE FLEET? — answerable only by joining Postgres to ClickHouse by
 * hand and recomputing the judge's arithmetic outside the judge.
 *
 * Doing that by hand has twice produced a wrong answer that LOOKED right: four impression counts
 * filled in from memory, which reported a sound verdict as suspect; and `e_value(wins, losses)`
 * called as `(decisive, wins)`, which returns a plausible number rather than raising. Both
 * happened while answering exactly this question.
 *
 * So NOTHING ON THIS PAGE IS COMPUTED FROM COUNTS. Every rate, null and e-value is read from
 * `progress`, which the tick writes from the same tallies its verdict came from. The page formats;
 * it does not derive. If you find yourself writing `wins / decisive` here, the number you want is
 * already in the payload under a name.
 *
 * 🔴 AND IT IS NOT THE PUBLIC PAGE WITH MORE DETAIL. It names every feed and its individual
 * counts. It is admin-only server-side and must stay that way.
 */
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { gymDiagnosticsQueryOptions, type GymProgress } from "@/lib/api";

export const Route = createFileRoute("/app/_sidebar-layout/gym-diagnostics")({
  component: RouteComponent,
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(gymDiagnosticsQueryOptions()).catch(() => null),
});

/* ------------------------------------------------------------------ formatting, never deriving */

function pct(value: number | null | undefined, places = 1): string {
  return value === null || value === undefined ? "—" : `${(value * 100).toFixed(places)}%`;
}

function num(value: number | null | undefined, places = 3): string {
  return value === null || value === undefined ? "—" : value.toFixed(places);
}

function when(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—";
}

function ago(value: string | null | undefined): string {
  if (!value) return "never";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 90) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

/** The bar, and what it means, said once. */
const BAR = 20;

/**
 * An e-value against the bar, as a shape as well as a number.
 *
 * 🔴 THE SCALE IS LOGARITHMIC BECAUSE THE QUANTITY IS. These run from 0.1 to five figures, and a
 * linear bar renders every interesting value as either nothing or full. What a reader needs is
 * distance from 20 in orders of magnitude.
 */
function EBar({ e }: { e: number | null | undefined }) {
  if (e === null || e === undefined) {
    return <span className="text-slate-dim">—</span>;
  }
  const decades = 4; // 0.01 .. 100x the bar
  const position = Math.max(0, Math.min(1, (Math.log10(e) + 2) / decades));
  const barAt = (Math.log10(BAR) + 2) / decades;
  const over = e >= BAR;
  return (
    <span className="flex items-center gap-2">
      <span className="tabular-nums w-16 shrink-0 text-right">
        {e >= 1000 ? e.toExponential(1) : num(e, 2)}
      </span>
      <span className="relative h-3 w-20 shrink-0 overflow-hidden rounded-[2px] bg-slate-3 dark:bg-slatedark-3">
        <span
          className={`absolute inset-y-0 left-0 ${over ? "bg-green-9 dark:bg-greendark-9" : "bg-slate-8 dark:bg-slatedark-8"}`}
          style={{ width: `${position * 100}%` }}
        />
        {/* the bar itself, drawn — an e-value with no 20 beside it means nothing */}
        <span
          className="absolute inset-y-0 w-px bg-amber-9 dark:bg-amberdark-9"
          style={{ left: `${barAt * 100}%` }}
        />
      </span>
    </span>
  );
}

function Chip({ tone, children }: { tone: "good" | "warn" | "bad" | "flat"; children: React.ReactNode }) {
  const styles = {
    good: "border-green-dim bg-green-subtle text-green-normal",
    warn: "border-amber-dim bg-amber-subtle text-amber-normal",
    bad: "border-red-dim bg-red-subtle text-red-normal",
    flat: "border-slate-dim bg-slate-subtle text-slate-dim",
  }[tone];
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs ${styles}`}>
      {children}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] tracking-[0.1em] text-slate-dim uppercase">{label}</span>
      <span className="text-sm text-slate-normal tabular-nums">{children}</span>
    </div>
  );
}

/**
 * The concentration question, answered before anything else on the card.
 *
 * Every experiment this fleet has run sits at roughly two-thirds one audience, so the pooled rate
 * is ambiguous between "one feed's readers" and "the fleet agrees". `corroborated` reduces that to
 * a boolean and this is the comparison behind it.
 */
function Concentration({ p, names }: { p: GymProgress; names: Record<string, string> }) {
  const top = p.top_feed;
  const w = p.without_top_feed;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-dim bg-slate-subtle p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-normal">Is this one feed or the fleet?</span>
        {p.corroborated === true ? (
          <Chip tone="good">the rest agree</Chip>
        ) : p.corroborated === false ? (
          <Chip tone="warn">not corroborated</Chip>
        ) : (
          <Chip tone="flat">no remainder to ask</Chip>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label="largest feed">
          {top !== null ? (names[String(top)] ?? `feed ${top}`) : "—"}
        </Field>
        <Field label="its share of evidence">{pct(p.top_feed_share)}</Field>
        <Field label="the rest, rate vs null">
          {pct(w.rate)} <span className="text-slate-dim">vs {pct(w.null)}</span>
        </Field>
        <Field label="the rest, e-value">
          <EBar e={w.e} />
        </Field>
      </div>
    </div>
  );
}

function PerFeed({ p, names }: { p: GymProgress; names: Record<string, string> }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[46rem] text-sm">
        <thead>
          <tr className="border-b border-slate-dim text-left text-[10px] tracking-[0.1em] text-slate-dim uppercase">
            <th className="py-2 pr-3 font-normal">feed</th>
            <th className="py-2 pr-3 text-right font-normal">decisive</th>
            <th className="py-2 pr-3 text-right font-normal">wins</th>
            <th className="py-2 pr-3 text-right font-normal">rate</th>
            {/* 🔴 ITS OWN null, not the pooled one. A feed whose candidate was shown more is not
                judged against a half — assuming one is the fault that published experiment 5. */}
            <th className="py-2 pr-3 text-right font-normal">its null</th>
            <th className="py-2 pr-3 text-right font-normal">shown c / x</th>
            <th className="py-2 pr-3 font-normal">e-value alone</th>
          </tr>
        </thead>
        <tbody>
          {p.feeds.map((f) => {
            const beats = f.rate !== null && f.null !== null && f.rate > f.null;
            return (
              <tr
                key={f.feed}
                className="border-b border-slate-6 last:border-b-0 dark:border-slatedark-6"
              >
                <td className="py-2 pr-3">
                  <span className="text-slate-normal">{names[String(f.feed)] ?? `feed ${f.feed}`}</span>
                  <span className="ml-2 text-xs text-slate-dim tabular-nums">{f.feed}</span>
                  {f.starving ? <span className="ml-2"><Chip tone="bad">starving</Chip></span> : null}
                  {f.feed === p.top_feed ? <span className="ml-2"><Chip tone="warn">largest</Chip></span> : null}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.decisive}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.wins}</td>
                <td
                  className={`py-2 pr-3 text-right tabular-nums ${beats ? "text-green-normal" : "text-slate-dim"}`}
                >
                  {pct(f.rate)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-dim tabular-nums">{pct(f.null)}</td>
                <td className="py-2 pr-3 text-right text-slate-dim tabular-nums">
                  {f.shown_control} / {f.shown_candidate}
                </td>
                <td className="py-2 pr-3">
                  <EBar e={f.e} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Progress({ p, names }: { p: GymProgress; names: Record<string, string> }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Field label="decisive">{p.decisive}</Field>
        <Field label="rate vs pooled null">
          {pct(p.win_rate)} <span className="text-slate-dim">vs {pct(p.null)}</span>
        </Field>
        <Field label="interval">
          {pct(p.low, 0)}–{pct(p.high, 0)}
        </Field>
        <Field label={`e-value (bar ${BAR})`}>
          <EBar e={p.e_value} />
        </Field>
        <Field label="more needed">{p.needed ?? "—"}</Field>
      </div>
      {p.blocking.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-dim">blocking:</span>
          {p.blocking.map((b) => (
            <Chip key={b} tone="warn">
              {b}
            </Chip>
          ))}
        </div>
      ) : null}
      <Concentration p={p} names={names} />
      <PerFeed p={p} names={names} />
    </div>
  );
}

function RouteComponent() {
  const { data, isPending, isError } = useQuery(gymDiagnosticsQueryOptions());

  if (isPending) {
    return <Shell>Loading…</Shell>;
  }
  // 404 is what a non-admin gets, and it is indistinguishable from a route that does not exist.
  if (isError || !data) {
    return <Shell>Not available. This view is restricted to gym operators.</Shell>;
  }

  const running = data.experiments.find((e) => e.status === "running");
  const closed = data.experiments.filter((e) => e.status !== "running" && e.status !== "pending");
  const instrument = data.tick?.instrument ?? null;

  return (
    <Shell>
      <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl text-slate-normal tracking-tight">Gym diagnostics</h1>
          <p className="max-w-prose text-sm text-slate-dim">
            Everything the public page withholds. Every number here is read from what the tick
            wrote, not recomputed — so it cannot disagree with the verdict.
          </p>
        </header>

        {/* Tick health first: a stale tick makes everything below it a lie about the present. */}
        <section className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border border-slate-dim bg-slate-subtle p-4">
          <Field label="last tick">
            {ago(data.tick?.ran_at)}
            <span className="ml-2 text-xs text-slate-dim">{when(data.tick?.ran_at)}</span>
          </Field>
          <Field label="ledger read up to">{when(data.tick?.watermark)}</Field>
          <Field label="instrument">
            {instrument ? (
              <span className="flex items-center gap-2">
                {instrument.wins}/{instrument.decisive}
                <span className="text-slate-dim">
                  vs {pct(instrument.null)} · min {instrument.minimum}
                </span>
                {instrument.state === "even" ? (
                  <Chip tone="good">even</Chip>
                ) : instrument.state === "biased" ? (
                  <Chip tone="bad">biased</Chip>
                ) : (
                  <Chip tone="flat">not yet</Chip>
                )}
              </span>
            ) : (
              "—"
            )}
          </Field>
          <Field label="feeds enrolled">{Object.keys(data.feeds).length}</Field>
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="text-lg text-slate-normal">Running</h2>
          {running ? (
            <div className="flex flex-col gap-4 rounded-lg border border-amber-dim bg-amber-subtle p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-slate-normal">{running.name}</span>
                <span className="text-xs text-slate-dim">
                  #{running.id} · opened {when(running.opened)}
                </span>
              </div>
              {running.progress ? (
                <Progress p={running.progress} names={data.feeds} />
              ) : (
                <p className="text-sm text-slate-dim">
                  No tick has recorded progress for this experiment yet.
                </p>
              )}
              {running.reason ? (
                <p className="border-t border-amber-dim pt-3 text-xs text-slate-dim">
                  {running.reason}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-dim">Nothing is running.</p>
          )}
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="text-lg text-slate-normal">Closed</h2>
          {closed.length === 0 ? (
            <p className="text-sm text-slate-dim">Nothing has closed yet.</p>
          ) : (
            <div className="flex flex-col gap-6">
              {closed.map((e) => (
                <div key={e.id} className="flex flex-col gap-3 border-b border-slate-dim pb-6 last:border-b-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-slate-normal">{e.name}</span>
                    <span className="flex items-center gap-2 text-xs text-slate-dim">
                      #{e.id} · closed {when(e.closed)}
                      <Chip tone={e.status === "positive" ? "good" : e.status === "abandoned" ? "bad" : "flat"}>
                        {e.status}
                      </Chip>
                    </span>
                  </div>
                  {/* `result` and never `progress` for a closed one: the result is frozen at the
                      verdict and the last progress row is stale by definition. */}
                  {e.result ? (
                    <Progress p={e.result} names={data.feeds} />
                  ) : (
                    <p className="text-sm text-slate-dim">
                      No result recorded — this one was withdrawn before it reached a verdict.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="instrument-sans mx-auto w-full max-w-6xl px-4 py-8 text-slate-normal md:px-8">
      {children}
    </div>
  );
}
